import { ChatInputState } from '@brandclub/bc-widgets';
import { IconArrowRight } from '../ConciergeIcons';
import React, { useState } from 'react';

interface SubmitButtonProps {
  disabled: boolean;
  isStreaming?: boolean;
  onStop?: () => void;
  styles: ChatInputState;
  resolveColor: (color: any) => string;
}

/** Relative luminance (0–1) of a hex or rgb()/rgba() color; null when unparseable. */
function relativeLuminance(color: string | undefined): number | null {
  if (!color) return null;
  let r: number, g: number, b: number;
  const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  const rgb = color.trim().match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  } else if (rgb) {
    r = Number(rgb[1]);
    g = Number(rgb[2]);
    b = Number(rgb[3]);
  } else {
    return null;
  }
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export const SubmitButton: React.FC<SubmitButtonProps> = ({
  disabled,
  isStreaming = false,
  onStop,
  styles,
  resolveColor,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const isDisabled = !isStreaming && disabled;

  const bgColor = resolveColor(styles.submitButtonBgColor);
  const backgroundColor = isHovered ? resolveColor(styles.submitButtonBgColorHover) : bgColor;
  const iconColor = resolveColor(styles.submitButtonIconColor);

  // Disabled state: the button background fades to 10% opacity over the input,
  // so the arrow must contrast with the INPUT background, not the button's.
  // Themes disagree on which token is the dark one (default: dark button bg +
  // light icon; some brands invert to a light button bg + dark icon), so pick
  // whichever of the two candidates contrasts more with the input background.
  const inputLuminance = relativeLuminance(resolveColor(styles.backgroundColor)) ?? 1;
  const bgLuminance = relativeLuminance(bgColor);
  const iconLuminance = relativeLuminance(iconColor);
  let disabledIconColor = bgColor;
  if (bgLuminance != null && iconLuminance != null) {
    disabledIconColor =
      Math.abs(iconLuminance - inputLuminance) > Math.abs(bgLuminance - inputLuminance) ? iconColor : bgColor;
  }

  const handleClick = (e: React.MouseEvent) => {
    if (isStreaming && onStop) {
      e.preventDefault();
      onStop();
    }
  };

  const size = styles.submitButtonSize;

  return (
    <button
      type={isStreaming ? 'button' : 'submit'}
      disabled={isDisabled}
      onClick={handleClick}
      aria-label={isStreaming ? 'Stop generating' : 'Submit'}
      data-concierge-submit
      className="tw:flex tw:items-center tw:justify-center tw:cursor-pointer disabled:tw:cursor-not-allowed"
      style={{
        width: size,
        height: size,
        position: 'relative',
        border: 'none',
        borderRadius: styles.submitButtonBorderRadius,
        background: 'transparent',
        padding: 0,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background layer — 20% opacity when disabled */}
      <span
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: styles.submitButtonBorderRadius,
          backgroundColor,
          opacity: isDisabled ? 0.1 : 1,
          transition: 'background-color 0.15s ease, opacity 0.15s ease',
          pointerEvents: 'none',
        }}
      />
      {/* Icon layer — when disabled, arrow uses the higher-contrast candidate at 50% */}
      <span
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isStreaming ? (
          <div
            style={{
              width: Math.round(size / 3),
              height: Math.round(size / 3),
              borderRadius: 1,
              backgroundColor: '#FFFFFF',
            }}
          />
        ) : (
          <IconArrowRight
            size={size / 2}
            style={{
              color: isDisabled ? disabledIconColor : iconColor,
              opacity: isDisabled ? 0.5 : 1,
              transition: 'color 0.15s ease, opacity 0.15s ease',
            }}
          />
        )}
      </span>
    </button>
  );
};
